import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { apiClient } from '../services/apiClient';
import { showToast } from '../store/toaster/action';
import { parseScreenshotDate } from '../components/Screenshot/screenshotUtils';
import {
  ScreenshotCaptureResponse,
  ScreenshotTimelineEntry,
  ScreenshotTimelineResponse,
} from '../types/ScreenshotTypes';

// ---------------------------------------------------------------------------
// Screenshot Collector service (WHEEL-394). Wheel only ever READS the
// per-domain timeline and WRITES through /refresh; /collect and /collect/bulk
// are for the backend triggers. `asset_id` in the API is Wheel's `threat_id`.
// ---------------------------------------------------------------------------

/** Timeline page size the detail page asks for by default. */
export const SCREENSHOT_TIMELINE_DEFAULT_LIMIT = 50;
/** Hard cap of the API's `limit` param - "Show all" asks for this. */
export const SCREENSHOT_TIMELINE_MAX_LIMIT = 200;

/** Refetch this long before the signed URLs expire so an image never 403s. */
const SAS_EXPIRY_BUFFER_MS = 60_000;
/** Fallback staleTime when the backend issues non-expiring URLs. */
const NON_EXPIRING_STALE_MS = 5 * 60_000;
/** Floor for the expiry-driven refetch interval - guards against a backend
 * signing very short-lived URLs turning into a tight refetch loop. */
const MIN_REFETCH_INTERVAL_MS = 15_000;

export const screenshotTimelineQueryKey = (threatId?: number, limit?: number) =>
  limit === undefined
    ? (['screenshots', 'timeline', threatId] as const)
    : (['screenshots', 'timeline', threatId, { limit }] as const);

export const fetchScreenshotTimeline = async (
  threatId: number,
  limit: number,
): Promise<ScreenshotTimelineResponse> => {
  const response = await apiClient({
    method: 'GET',
    url: `v2/screenshots/assets/${threatId}/timeline?limit=${limit}&offset=0`,
  });
  return response.data;
};

/** Milliseconds until the earliest signed URL in `items` expires, or null
 * when none of them expire. */
export const msUntilSasExpiry = (
  items: Pick<ScreenshotTimelineEntry, 'sas_expires_at'>[],
  now: number = Date.now(),
): number | null => {
  const expiries = items
    .map(item => parseScreenshotDate(item.sas_expires_at))
    .filter((date): date is Date => !!date)
    .map(date => date.getTime());
  if (expiries.length === 0) return null;
  return Math.min(...expiries) - now;
};

interface TimelineQueryOptions {
  limit?: number;
  enabled?: boolean;
}

/**
 * A domain's screenshot history, newest first. The signed URLs inside expire,
 * so staleness and the background refetch are both driven by the earliest
 * `sas_expires_at` in the page: consumers keep rendering the cached entries
 * and the URLs are silently swapped before they stop working.
 */
export const useScreenshotTimelineQuery = (
  threatId?: number,
  {
    limit = SCREENSHOT_TIMELINE_DEFAULT_LIMIT,
    enabled = true,
  }: TimelineQueryOptions = {},
) =>
  useQuery<ScreenshotTimelineResponse>({
    queryKey: screenshotTimelineQueryKey(threatId, limit),
    queryFn: () => fetchScreenshotTimeline(threatId as number, limit),
    enabled: enabled && typeof threatId === 'number',
    // 404 = domain unknown to the screenshot service. Treat as "no screenshots"
    // instead of an error so consumers render the empty state.
    retry: false,
    staleTime: query => {
      const data = query.state.data as ScreenshotTimelineResponse | undefined;
      if (!data) return 0;
      const ms = msUntilSasExpiry(data.items);
      if (ms === null) return NON_EXPIRING_STALE_MS;
      return Math.max(0, ms - SAS_EXPIRY_BUFFER_MS);
    },
    refetchInterval: query => {
      const data = query.state.data as ScreenshotTimelineResponse | undefined;
      if (!data || data.items.length === 0) return false;
      const ms = msUntilSasExpiry(data.items);
      if (ms === null) return false;
      return Math.max(MIN_REFETCH_INTERVAL_MS, ms - SAS_EXPIRY_BUFFER_MS);
    },
  });

/** Normalises a 404 ("domain not found") into an empty timeline. */
export const isScreenshotNotFound = (error: unknown): boolean =>
  (error as any)?.response?.status === 404;

const captureToTimelineEntry = (
  capture: ScreenshotCaptureResponse,
): ScreenshotTimelineEntry => ({
  id: capture.id,
  status: 'manual_refresh',
  created: capture.created,
  url: capture.url,
  thumbnail_url: capture.thumbnail_url,
  sas_expires_at: capture.sas_expires_at,
  capture_profile: capture.capture_profile,
  final_url: capture.final_url,
});

/** Seconds from a 429's Retry-After header, or null when absent/invalid. */
export const getRetryAfterSeconds = (error: unknown): number | null => {
  const raw = (error as any)?.response?.headers?.['retry-after'];
  if (raw === undefined || raw === null) return null;
  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds >= 0 ? Math.ceil(seconds) : null;
};

export const refreshScreenshotErrorMessage = (error: unknown): string => {
  const status = (error as any)?.response?.status;
  switch (status) {
    case 429: {
      const seconds = getRetryAfterSeconds(error);
      return seconds
        ? `Refresh limit reached. Try again in ${seconds}s.`
        : 'Refresh limit reached. Try again in a minute.';
    }
    case 409:
      return 'A capture is already running for this domain. Try again shortly.';
    case 400:
      return 'This domain cannot be captured.';
    case 404:
      return 'This domain is unknown to the screenshot service.';
    case 401:
      return 'Session expired. Please log in again.';
    default:
      return 'Capture failed. The site did not render.';
  }
};

/**
 * Forces a fresh capture (recorded as `manual_refresh`). On success
 * the new capture is prepended into every cached timeline page for the
 * domain so the detail page and any expanded row agree without a refetch.
 */
export const useRefreshScreenshotMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation<ScreenshotCaptureResponse, unknown, number>({
    mutationFn: async threatId => {
      const response = await apiClient({
        method: 'POST',
        url: `v2/screenshots/assets/${threatId}/refresh`,
      });
      return response.data;
    },
    onSuccess: (capture, threatId) => {
      const entry = captureToTimelineEntry(capture);
      const key = screenshotTimelineQueryKey(threatId);
      const cached = queryClient.getQueriesData<ScreenshotTimelineResponse>({
        queryKey: key,
      });
      if (cached.length === 0) {
        queryClient.invalidateQueries({ queryKey: key });
        return;
      }
      queryClient.setQueriesData<ScreenshotTimelineResponse>(
        { queryKey: key },
        (old: ScreenshotTimelineResponse | undefined) => {
          if (!old) return old;
          const items = [
            entry,
            ...old.items.filter(
              (item: ScreenshotTimelineEntry) => item.id !== entry.id,
            ),
          ];
          return {
            ...old,
            total: old.total + 1,
            items: items.slice(0, Math.max(old.limit, 1)),
          };
        },
      );
    },
    onError: error => {
      dispatch(showToast(refreshScreenshotErrorMessage(error), 'error'));
    },
  });
};
